import { sharedConfig } from '@web-publish-id/shared/config'

/**
 * Bridge配置选项
 */
interface BridgeOptions {
  isHost: boolean // 是否是主页面（iframe中的页面）
  iframeId?: string // 客户端模式下可选，iframe的id

  onload: () => void
  onerror: (e: any) => void
}

/**
 * 消息格式
 */
interface Message {
  type: string // 消息类型
  payload: any // 消息内容
  error?: any // 错误信息
  reqId?: string // 请求ID，用于请求发送
  resId?: string // 请求ID，用于响应请求
}

/**
 * 事件处理器类型
 */
type EventHandler = (payload: any) => void

/**
 * 跨iframe通信桥接器
 * 支持主页面（iframe）和客户端页面之间的双向通信
 */
export class Bridge {
  private iframe: HTMLIFrameElement | null = null // iframe元素
  private hostUrl: string = sharedConfig.origin + '/web/bridge.html?t=' + Date.now() // 主页面的url
  private messageHandlers: Map<string, Function> = new Map() // 请求处理器
  private eventHandlers: Map<string, Set<EventHandler>> = new Map() // 事件处理器
  private callEventHandler: Map<string, EventHandler> = new Map() // 调用事件处理器
  private isHost: boolean // 是否是主页面
  private target: Window | null = null // 消息发送目标窗口
  private isConnected: boolean = false

  constructor() {}

  init(options: BridgeOptions) {
    this.isHost = options.isHost

    if (!options.isHost) {
      const iframe = this.initIframe(options)
      this.target = iframe!.contentWindow || null
      this.setupMessageListener()

      // 监听iframe加载完成事件
      this.on('connected', () => options.onload())

      iframe.onload = () => {}

      // 添加错误处理
      iframe.onerror = (e) => {
        console.error('Failed to load iframe')
        this.destroy()
        options.onerror(e)
      }
    } else {
      this.target = window.parent
      this.isConnected = this.setupMessageListener()
      options.onload()
    }
  }

  /**
   * 初始化iframe
   */
  private initIframe(options: BridgeOptions) {
    const iframeId = options.iframeId || 'seaart-auth-sdk-iframe'
    this.iframe = document.createElement('iframe')
    this.iframe.id = iframeId
    this.iframe.src = this.hostUrl
    this.iframe.style.display = 'none'
    this.iframe.style.visibility = 'hidden'
    this.iframe.allow = 'identity-credentials-get'

    document.body.appendChild(this.iframe)
    return this.iframe
  }

  /**
   * 设置消息监听器
   */
  private setupMessageListener() {
    if (!this.target) return false

    window.addEventListener('message', (event) => {
      if (event.source !== this.target) return

      const message = event.data as Message
      console.debug('[on message] :>> ', message)

      if (!this.isHost && !this.isConnected) {
        if (message.type === 'ready') {
          this.isConnected = true
          this.dispatchEvent('connected', null)
        }
        return
      }

      // 请求call处理
      if (message.reqId) {
        this.dispatchCallEvent(message.type, message)
        return
      }

      // 响应call处理
      if (message.resId) {
        this.dispatchCallRes(message)
        return
      }

      this.dispatchEvent(message.type, message.payload)
    })

    return true
  }

  /**
   * 发送消息
   */
  private postMessage(message: Message) {
    console.debug('[postMessage] :>> ', message)
    if (!this.target) return
    this.target.postMessage(message, '*')
  }

  /**
   * 发送消息并等待响应
   * @param type 消息类型
   * @param payload 消息数据
   * @returns Promise<T> 响应数据
   */
  public call<T>(type: string, payload?: any): Promise<T> {
    return new Promise((resolve, reject) => {
      const messageId = Math.random().toString(36).substring(2, 11)
      const message: Message = {
        type,
        payload,
        reqId: messageId,
      }

      const handler = (payload: any, error?: any) => {
        this.messageHandlers.delete(messageId)
        if (error) {
          reject(error)
        } else {
          resolve(payload)
        }
      }

      this.messageHandlers.set(messageId, handler)
      this.postMessage(message)
    })
  }

  /**
   * 分发调用响应
   * @param message 消息
   */
  private dispatchCallRes(message: Message) {
    const handler = this.messageHandlers.get(message.resId)

    if (handler) {
      try {
        handler(message.payload, message.error)
      } catch (e) {}
    }
  }

  /**
   * 调用方法
   * @param method 方法名
   * @param args 方法参数
   * @returns Promise<T> 响应数据
   */
  public callMethod<T>(method: string, ...args: any[]): Promise<T> {
    return this.call('method', { method, args })
  }

  /**
   * 发送消息（不等待响应）
   * @param type 消息类型
   * @param payload 消息数据
   */
  public emit(type: string, payload?: any) {
    this.postMessage({ type, payload })
  }

  /**
   * 注册事件监听器
   * @param type 事件类型
   * @param handler 事件处理器
   */
  public on(type: string, handler: EventHandler) {
    if (!this.eventHandlers.has(type)) {
      this.eventHandlers.set(type, new Set())
    }
    this.eventHandlers.get(type)!.add(handler)
  }

  /**
   * 注册调用事件监听器
   * @param type 事件类型
   * @param handler 事件处理器
   */
  public onCall(type: string, handler: EventHandler) {
    this.callEventHandler.set(type, handler)
  }

  /**
   * 移除事件监听器
   * @param type 事件类型
   * @param handler 事件处理器
   */
  public off(type: string, handler: EventHandler) {
    this.eventHandlers.get(type)?.delete(handler)
  }

  /**
   * 移除调用事件监听器
   * @param type 事件类型
   */
  public offCall(type: string) {
    this.callEventHandler.delete(type)
  }

  /**
   * 触发事件
   */
  private dispatchEvent(type: string, payload: any) {
    this.eventHandlers.get(type)?.forEach((handler) => {
      handler(payload)
    })
  }

  /**
   * 分发调用事件
   * @param type 事件类型
   * @param payload 事件数据
   * @returns 事件处理结果
   */
  private async dispatchCallEvent(type: string, message: Message) {
    const handler = this.callEventHandler.get(type)
    let result = null
    let error = null
    try {
      result = await handler?.(message.payload)
    } catch (e) {
      error = e
    }

    this.postMessage({ type, payload: result, error, resId: message.reqId })
  }

  /**
   * 销毁Bridge实例
   */
  public destroy() {
    if (this.iframe) {
      document.body.removeChild(this.iframe)
      this.iframe = null
    }
    this.messageHandlers.clear()
    this.eventHandlers.clear()
    this.target = null
  }
}
